// Helpers for the comment sentiment panel (SENTIMENT_UGC_API.md).
import { SENTIMENT_PLATFORMS } from "../../constants"

// Sentiment timestamps may or may not carry a zone - the video endpoints send
// bare UTC with no "Z" (see utils.js formatUtcDate), so only append one when
// the string doesn't already have a zone of its own.
const parseApiDate = (value) => {
  if (!value) return null
  const str = String(value)
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(str)
  const date = new Date(hasZone ? str : `${str}Z`)
  return Number.isNaN(date.getTime()) ? null : date
}

// The "Refreshes" date shown on the panel - the earliest `expires_at` across
// completed platforms (each platform expires with its videos' 3-month refresh).
export const getNextSentimentRefresh = (data) => {
  const expiries = Object.values(data?.platforms || {})
    .filter(p => p?.status === "completed")
    .map(p => parseApiDate(p.expires_at))
    .filter(Boolean)
    .sort((a, b) => a - b)
  return expiries[0] || null
}

export const formatSentimentDate = (value) => {
  const date = parseApiDate(value)
  return date ? date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : null
}

// `score` is (pos - neg) / labelled on -1..1 - shown as a signed -100..+100
// "net sentiment" so it reads like a percentage-point lead.
export const toNetScore = (score) => {
  if (score === null || score === undefined || Number.isNaN(Number(score))) return null
  return Math.round(Number(score) * 100)
}

export const formatNetScore = (score) => {
  const net = toNetScore(score)
  if (net === null) return "—"
  return net > 0 ? `+${net}` : String(net)
}

export const formatPercent = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "0%"
  const num = Number(value)
  return `${num % 1 === 0 ? num : num.toFixed(1)}%`
}

// "tier_2" -> "Tier 2"
export const formatTier = (tier) => {
  if (!tier) return null
  return String(tier).replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
}

// praise/complaints/feature_requests/bugs_or_issues aren't given an item shape
// in the doc - accept plain strings or objects using any of the usual keys.
export const normalizeInsightItem = (item) => {
  if (item === null || item === undefined) return null
  if (typeof item === "string") return { text: item }
  if (typeof item !== "object") return { text: String(item) }
  const text = item.text || item.point || item.title || item.theme || item.issue || item.request
    || item.feature || item.bug || item.summary || item.name || item.description
  if (!text) return null
  const detail = [item.description, item.context, item.detail].find(d => d && d !== text) || null
  const mentions = item.mentions ?? item.count ?? item.frequency ?? null
  const examples = Array.isArray(item.examples) ? item.examples : []
  return { text, detail, mentions, examples, sentiment: item.sentiment || null }
}

export const normalizeInsightList = (list) =>
  (Array.isArray(list) ? list : []).map(normalizeInsightItem).filter(Boolean)

const normalizeComment = (comment, platform) => {
  if (!comment) return null
  if (typeof comment === "string") return { text: comment, platform, sentiment: "unknown", likes: 0, replies: 0 }
  const text = comment.text || comment.comment || comment.content || comment.body
  if (!text) return null
  return {
    text,
    platform: comment.platform || platform,
    sentiment: comment.sentiment || comment.label || "unknown",
    likes: Number(comment.likes ?? comment.like_count ?? comment.likes_count ?? 0) || 0,
    replies: Number(comment.replies ?? comment.reply_count ?? comment.replies_count ?? 0) || 0,
    author: comment.author || comment.username || comment.author_name || null,
    video_id: comment.video_id || null,
  }
}

// `include_comments=true` adds the 100 analysed comments per platform. The doc
// doesn't pin where they sit, so this reads either `platforms.<p>.comments` or
// a top-level `comments` (an array, or keyed by platform).
export const extractComments = (data, platform) => {
  const fromPlatform = data?.platforms?.[platform]?.comments
  let raw = Array.isArray(fromPlatform) ? fromPlatform : null
  if (!raw) {
    const top = data?.comments
    if (Array.isArray(top)) raw = top.filter(c => !c?.platform || c.platform === platform)
    else if (top && Array.isArray(top[platform])) raw = top[platform]
  }
  return (raw || []).map(c => normalizeComment(c, platform)).filter(Boolean)
}

// video_id -> the analysed video, so quotes/examples can link back to it.
export const buildVideoIndex = (platformData) => {
  const index = {}
  ;(platformData?.videos || []).forEach((video) => {
    if (video?.video_id) index[video.video_id] = video
  })
  return index
}

export const hasAnyCounts = (sentiment) =>
  !!sentiment && Object.values(sentiment.counts || sentiment.percent || {}).some(v => Number(v) > 0)

// Platforms in display order, including ones the response omitted but the
// backend listed as blocked (expired for a limited tier).
export const getPlatformEntries = (data) => {
  const platforms = data?.platforms || {}
  const progress = data?.progress || {}
  const blocked = Array.isArray(data?.blocked_platforms) ? data.blocked_platforms : []
  const ids = [...SENTIMENT_PLATFORMS, ...Object.keys(platforms).filter(id => !SENTIMENT_PLATFORMS.includes(id))]
  return ids
    .filter(id => platforms[id] || progress[id] || blocked.includes(id))
    .map(id => ({
      id,
      data: platforms[id] || { status: blocked.includes(id) ? "expired" : "pending" },
      stage: progress[id] || null,
      blocked: blocked.includes(id),
    }))
}
